import { useQuery } from 'react-query';
import { useAxiosClient } from '../utils/useAxiosClient';

export interface HourData {
  hour: number;
  value: number;
  count: number;
}

export interface DayData {
  day: string;
  hours: HourData[];
}

export interface BestTimeToPostData {
  heatmap_data: DayData[];
  timezone: string;
}

export function useBestTimeToPost(
  orgId: number,
  selectedPillars: string[] = [],
  startDate: string | null = null,
  endDate: string | null = null,
  selectedBrands: string[] = []
) {
  const axiosClient = useAxiosClient();
  
  // Get user's timezone
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Build the query parameters
  const queryParams = new URLSearchParams();
  if (orgId) queryParams.append('org', orgId.toString());
  
  // Add pillars if selected
  if (selectedPillars && selectedPillars.length > 0) {
    selectedPillars.forEach(pillar => {
      queryParams.append('pillars', pillar);
    });
  }
  
  // Add brands if selected
  if (selectedBrands && selectedBrands.length > 0) {
    selectedBrands.forEach(brand => {
      queryParams.append('brands', brand);
    });
  }
  
  // Add date range parameters if provided
  if (startDate) queryParams.append('start_date', startDate);
  if (endDate) queryParams.append('end_date', endDate);
  
  // Add user's timezone
  queryParams.append('timezone', timezone);

  // React Query automatically caches by the key
  const {
    data,
    isLoading,
    error,
  } = useQuery(
    ['bestTimeToPost', orgId, selectedPillars, startDate, endDate, selectedBrands, timezone],
    async () => {
      if (!orgId) {
        throw new Error('No organization selected');
      }

      const response = await axiosClient.get<BestTimeToPostData>(
        `/api/best-time-to-post?${queryParams.toString()}`
      );

      // If the response has an 'error' field, throw an error
      if ('error' in response.data) {
        throw new Error(
          typeof response.data.error === 'string'
            ? response.data.error
            : 'An error occurred fetching best time to post data'
        );
      }

      return response.data;
    },
    {
      enabled: !!orgId,
      staleTime: 0, // No stale time - always refresh when params change
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      retry: 1,
    }
  );

  // Return in the same format as other hooks
  return {
    data: data ?? null,
    loading: isLoading,
    error: error ? (error as Error).message : null,
  };
} 